import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions, featuresActions, sharedActions } from '../../../store';
import { router } from '../../../App';
import { useUrlSync } from '../../../hooks/useUrlSync';
import { useActiveProfile } from '../../../hooks/useActiveProfile';
import SurfaceButtons from '../SurfaceButtons/SurfaceButtons';
import ProductList from '../../shared/ProductList/ProductList';
import DropdownFilter from '../../shared/DropdownFilter/DropdownFilter';
import PaintPackages from '../PaintPackages/PaintPackages';

import styles from "./ProductMenu.module.css";


export default function ProductMenu() {

    const dispatch = useDispatch();
    
    useUrlSync();
    useActiveProfile();
    
    const activeTab = useSelector(state => state.shared.menuActiveTab);
    const activeSeriesId = useSelector(state => state.features.activeSeriesId);
    const activeSeriesIds = useSelector(state => state.features.activeSeriesIds);
    const activeHardcodedFilters = useSelector(state => state.features.activeHardcodedFilters);
    const activeDropdownColumn = useSelector(state => state.features.activeDropdownColumn);
    const activeDropdownColumnTwo = useSelector(state => state.features.activeDropdownColumnTwo);
    const activeGroupByColumn = useSelector(state => state.features.activeGroupByColumn);
    const activeGroupByColumnOrder = useSelector(state => state.features.activeGroupByColumnOrder);
    const selectedExteriorSurface = useSelector(state => state.features.selectedSurfaces.exterior);
    const selectedKitchenSurface = useSelector(state => state.features.selectedSurfaces.kitchen);
    const exteriorAppliedProducts = useSelector(state => state.features.exteriorAppliedProducts);
    const kitchenAppliedProducts = useSelector(state => state.features.kitchenAppliedProducts);
    const vizDataByTab = useSelector(state => state.features.vizDataByTab);
    const selectedInteriorFloorPlan = useSelector(state => state.features.selectedInteriorFloorPlan);
    const selectedPaintPackage = useSelector(state => state.features.selectedPaintPackage);
    
    // Get the selected product ID for the current tab and surface
    const getSelectedProductId = () => {
        if (activeTab === 'exterior' && selectedExteriorSurface) {
            return exteriorAppliedProducts[selectedExteriorSurface]?.id || null;
        } else if (activeTab === 'kitchen' && selectedKitchenSurface) {
            return kitchenAppliedProducts[selectedKitchenSurface]?.id || null;
        }
        return null;
    };
    
    // Filter states
    const [filter1Value, setFilter1Value] = useState('-1');
    const [filter2Value, setFilter2Value] = useState('-1');
    const [productData, setProductData] = useState([]);
    
    // Use Redux state for paint packages visibility instead of local state
    const paintPackagesActive = useSelector(state => state.features.paintPackagesActive);

    // Reset filters when series or hardcoded filters change
    useEffect(() => {
        setFilter1Value('-1');
        setFilter2Value('-1');
    }, [activeSeriesId, activeSeriesIds, activeHardcodedFilters]);




    // Fetch product data for dropdown filters (supports multiple series)
    useEffect(() => {
        // Determine effective series IDs
        const seriesIdsToFetch = activeSeriesIds || (activeSeriesId ? [activeSeriesId] : []);
        
        if (seriesIdsToFetch.length === 0) {
            setProductData([]);
            return;
        }

        // Fetch all series in parallel
        const fetchPromises = seriesIdsToFetch.map(id => {
            const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/${id}.json`;
            return fetch(URL)
                .then(response => response.json())
                .then(data => Array.isArray(data) ? data : [])
                .catch(() => []);
        });

        Promise.all(fetchPromises)
            .then(results => {
                const mergedProducts = results.flat();
                setProductData(mergedProducts);
            })
            .catch((error) => {
                console.error('Error fetching products for filters:', error);
                setProductData([]);
            });
    }, [activeSeriesId, activeSeriesIds]);

    const setActiveTab = (tab) => {
        // Save current tab's vizData before switching
        const chamViz = document.getElementById('chamViz');
        let savedSpecifier = vizDataByTab[activeTab]?.specifier;
        let savedSel = vizDataByTab[activeTab]?.sel;
        
        if (chamViz) {
            const vizData = chamViz.getSaveData();
            if (vizData) {
                savedSpecifier = vizData.specifier;
                savedSel = vizData.sel;
                dispatch(featuresActions.setVizDataForTab({
                    tab: activeTab,
                    specifier: vizData.specifier,
                    sel: vizData.sel
                }));
            }
        }
        
        dispatch(appActions.setMenuActiveTab(tab));
        // Sync the activeSeriesId for the new tab's selected surface
        dispatch(featuresActions.syncActiveSeriesForTab(tab));
        
        // Get the stored vizData for the new tab
        const newTabVizData = vizDataByTab[tab];
        const spec = newTabVizData.specifier;
        const sel = newTabVizData.sel;
        
        // Navigate to update the URL with the new tab's specifier and sel
        // Use a single '-1' if the stored sel is all defaults (e.g. '-1,-1,-1,-1')
        const normalizedSel = sel.split(',').every(s => s.trim() === '-1') ? '-1' : sel;
        
        router.navigate({
            pathname: `/visualizer/spec/${encodeURIComponent(spec)}/sel/${normalizedSel}`,
            search: window.location.search,
            hash: window.location.hash,
        });
    };

    const handleKitchenCabinetProductSelect = (product) => {
        console.log('cabinet Product selected:', product);

        let color = product.id;
        let door = product.descriptor.split(",")[0].trim();
        let drawer = product.descriptor.split(",")[1].trim();
        
        document.getElementById('chamViz').applyByFilter("", "", function (newSel, currentSelectedSurface) {
             dispatch(sharedActions.setVizLoading(true));
            if (this.name === "Cabinet Doors") {
                dispatch(sharedActions.setVizLoading(true));
                return `${door}`;
            } else if (this.name === "Cabinet Base") {
                 dispatch(sharedActions.setVizLoading(true));
                return `${drawer}`;
            }
            
        }, true);
    };

    const handleProductSelect = (product) => {
   
        // Get the current surface for the active tab
        const currentSurface = activeTab === 'exterior' ? selectedExteriorSurface : selectedKitchenSurface;

        // Track the applied product in the store
        if (currentSurface) {
            dispatch(featuresActions.setAppliedProduct({
                tab: activeTab,
                surface: currentSurface,
                product: product
            }));
        }

        if(product.categoryName == 'Cabinet Doors'){
            handleKitchenCabinetProductSelect(product);
            return;
        }
        
        // Map UI surface names to visualizer surface names
        // 'Stone/Brick' button applies to 'Stone' surface in the visualizer
        const vizSurfaceName = currentSurface === 'Stone/Brick' ? 'Stone' : currentSurface;
        
        document.getElementById('chamViz').applyByFilter('', '', function (newSel) {
            if (this.name == vizSurfaceName) {
                 dispatch(sharedActions.setVizLoading(true));
                 return product.id
            }
        },true);
    };

    const handleFilter1Change = (e) => {
        setFilter1Value(e.target.value);
    };

    const handleFilter2Change = (e) => {
        setFilter2Value(e.target.value);
    };

    const handleSurfaceClick = () => {
        // Surface selection is handled by SurfaceButtons component via Redux
        // paintPackagesActive will be set to false by setSelectedSurfaceForTab reducer
    };

    const handlePaintPackagesClick = () => {
        // Clear surface selection so no surface button appears selected
        // This also sets paintPackagesActive to true in Redux
        dispatch(featuresActions.clearSelectedSurfaceForTab(activeTab));
    };

    // Build filters object for ProductList (user-selected dropdown filters only)
    const buildFilters = () => {
        const filters = {};
        
        // Apply user-selected dropdown filters
        if (filter1Value !== '-1') {
            filters[activeDropdownColumn] = filter1Value;
        }
        if (filter2Value !== '-1') {
            filters[activeDropdownColumnTwo] = filter2Value;
        }
        return filters;
    };


    return (
        <section className={styles.productMenu}>
            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <button
                    className={`${styles.tab} ${activeTab === 'exterior' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('exterior')}
                >
                    Exterior
                </button>
                {selectedInteriorFloorPlan && (
                    <button
                        className={`${styles.tab} ${activeTab === 'kitchen' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('kitchen')}
                    >
                        Kitchen
                    </button>
                )}
            </div>

            {/* exterior Tab Content */}
            {activeTab === 'exterior' && (
                <>
                    <div className={styles.surfaceButtonsRow}>
                        <button 
                            className={`${styles.paintPackagesButton} ${paintPackagesActive ? styles.selected : ''}`}
                            onClick={handlePaintPackagesClick}
                        >
                            Paint Packages
                        </button>
                        <SurfaceButtons tab="exterior" onSurfaceClick={handleSurfaceClick} />
                    </div>
                    
                    {paintPackagesActive ? (
                        <PaintPackages />
                    ) : selectedExteriorSurface === 'Front Door' ? (
                        // Front Door shows only the accent products from the selected paint package
                        selectedPaintPackage?.accent1ProductId || selectedPaintPackage?.accent2ProductId ? (
                            <div className={styles.productListContainer}>
                                <ProductList 
                                    seriesId={activeSeriesId.toString()}
                                    thumbSize="128"
                                    allowedIds={[
                                        selectedPaintPackage.accent1ProductId,
                                        selectedPaintPackage.accent2ProductId
                                    ].filter(Boolean)}
                                    onProductSelect={handleProductSelect}
                                    selectedProductId={getSelectedProductId()}
                                />
                            </div>
                        ) : (
                            <div className={styles.productListContainer}>
                                <p style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                                    Please select a Paint Package first to see Front Door options.
                                </p>
                            </div>
                        )
                    ) : (
                        (activeSeriesId || activeSeriesIds) && (
                            <>
                                <div className={styles.filtersContainer}>
                                    <DropdownFilter
                                        handleSelect={handleFilter1Change}
                                        label="Types"
                                        value={filter1Value}
                                        column={activeDropdownColumn}
                                        data={productData}
                                        hardcodedFilters={activeHardcodedFilters}
                                        className={styles.filterDropdown}
                                    />
                                    <DropdownFilter
                                        handleSelect={handleFilter2Change}
                                        label="Colors"
                                        value={filter2Value}
                                        column={activeDropdownColumnTwo}
                                        data={productData}
                                        hardcodedFilters={activeHardcodedFilters}
                                        filterBy={filter1Value !== '-1' ? { [activeDropdownColumn]: filter1Value } : null}
                                        className={styles.filterDropdown}
                                    />
                                </div>
                                <div className={styles.productListContainer}>
                                    <ProductList 
                                        seriesId={activeSeriesId ? activeSeriesId.toString() : undefined}
                                        seriesIds={activeSeriesIds}
                                        thumbSize="128"
                                        filters={buildFilters()}
                                        hardcodedFilters={activeHardcodedFilters}
                                        onProductSelect={handleProductSelect}
                                        selectedProductId={getSelectedProductId()}
                                        groupByColumn={activeGroupByColumn}
                                        groupByColumnOrder={activeGroupByColumnOrder}
                                    />
                                </div>
                            </>
                        )
                    )}
                </>
            )}

            {/* kitchen Tab Content */}
            {activeTab === 'kitchen' && (
                <>
                    <SurfaceButtons tab="kitchen" />
                    {activeSeriesId && (
                        <>
                            <div className={styles.filtersContainer}>
                                <DropdownFilter
                                    handleSelect={handleFilter1Change}
                                    label="Type"
                                    value={filter1Value}
                                    column={activeDropdownColumn}
                                    data={productData}
                                    hardcodedFilters={activeHardcodedFilters}
                                    className={styles.filterDropdown}
                                />
                                <DropdownFilter
                                    handleSelect={handleFilter2Change}
                                    label="Colors"
                                    value={filter2Value}
                                    column={activeDropdownColumnTwo}
                                    data={productData}
                                    hardcodedFilters={activeHardcodedFilters}
                                    filterBy={filter1Value !== '-1' ? { [activeDropdownColumn]: filter1Value } : null}
                                    className={styles.filterDropdown}
                                />
                            </div>

                            <div className={styles.productListContainer}>
                                <ProductList 
                                    seriesId={activeSeriesId.toString()}
                                    thumbSize="128"
                                    groupByWarranty={true}
                                    filters={buildFilters()}
                                    hardcodedFilters={activeHardcodedFilters}
                                    onProductSelect={handleProductSelect}
                                    selectedProductId={getSelectedProductId()}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </section>
    );
}
